import type { ModuleGridType, StudioSystemType } from 'types/studio/items'

import { getVanDerValkSuitabilityPayload } from './getVanDerValkSuitabilityPayload'
import { getVanDerValkModuleGridsForBom } from './vanDerValkModuleGrids'

/** Studio quick-roof modes supported for Van der Valk on-roof (includes dormer variants). */
export const VANDERVALK_ALLOWED_OS_STRUCTURE_MODES = [
  'aframe',
  'hip',
  'shed',
  'aframe_dormer',
  'hip_dormer',
  'shed_dormer',
] as const

export type VanDerValkAllowedOsStructureMode = (typeof VANDERVALK_ALLOWED_OS_STRUCTURE_MODES)[number]

export type VanDerValkPitchedRoofStructureIssueReason =
  | 'no_roof_facet'
  | 'unsupported_manual_roof'
  | 'unsupported_roof_structure'

const ALLOWED_MODE_SET = new Set<string>(VANDERVALK_ALLOWED_OS_STRUCTURE_MODES)

type VanDerValkFacetForStructure = {
  uuid?: string
  isManaged?: boolean
  getStructure?: () => { mode?: string } | null | undefined
}

function facetFromGrid(grid: ModuleGridType): VanDerValkFacetForStructure | null {
  const facet = grid.facet as VanDerValkFacetForStructure | null | undefined
  if (!facet) {
    return null
  }
  return facet
}

function collectUniqueFacetsFromGrids(grids: ModuleGridType[]): VanDerValkFacetForStructure[] {
  const byUuid = new Map<string, VanDerValkFacetForStructure>()
  for (const grid of grids) {
    const facet = facetFromGrid(grid)
    if (!facet) {
      continue
    }
    const key = facet.uuid != null ? String(facet.uuid) : `__anon_${byUuid.size}`
    if (!byUuid.has(key)) {
      byUuid.set(key, facet)
    }
  }
  return [...byUuid.values()]
}

function issueForFacet(facet: VanDerValkFacetForStructure | null): VanDerValkPitchedRoofStructureIssueReason | null {
  if (!facet) {
    return 'no_roof_facet'
  }
  if (facet.isManaged !== true) {
    return 'unsupported_manual_roof'
  }
  const structure = typeof facet.getStructure === 'function' ? facet.getStructure() : null
  if (!structure) {
    return 'unsupported_manual_roof'
  }
  const mode = structure.mode
  if (typeof mode !== 'string' || !ALLOWED_MODE_SET.has(mode)) {
    return 'unsupported_roof_structure'
  }
  return null
}

/**
 * First on-roof roof-structure issue for active module grids, or null when valid / not applicable.
 * Requires valid panel specs and at least one active grid (same early exit as other mounting-tab checks).
 */
export function getVanDerValkPitchedRoofStructureIssue(
  system: StudioSystemType | null | undefined
): VanDerValkPitchedRoofStructureIssueReason | null {
  if (!system || system.mounting !== 'vanDerValk' || system.mounting_type !== 'onRoof') {
    return null
  }

  const pre = getVanDerValkSuitabilityPayload(system)
  if (!pre.ok) {
    return null
  }

  const grids = getVanDerValkModuleGridsForBom(system)
  if (!grids.length) {
    return null
  }

  let sawMissingFacet = false
  for (const grid of grids) {
    const facet = facetFromGrid(grid)
    const issue = issueForFacet(facet)
    if (issue === 'no_roof_facet') {
      sawMissingFacet = true
      continue
    }
    if (issue != null) {
      return issue
    }
  }

  if (sawMissingFacet) {
    return 'no_roof_facet'
  }

  const uniqueFacets = collectUniqueFacetsFromGrids(grids)
  if (!uniqueFacets.length) {
    return 'no_roof_facet'
  }

  for (const facet of uniqueFacets) {
    const issue = issueForFacet(facet)
    if (issue != null) {
      return issue
    }
  }

  return null
}

export function isVanDerValkPitchedRoofStructureReady(system: StudioSystemType | null | undefined): boolean {
  return getVanDerValkPitchedRoofStructureIssue(system) == null
}
